import React, { useState } from 'react';
import BASE_URL, { SEO_BASE_URL } from '../config';
import {
  FaBook, FaChevronDown, FaPlane, FaEdit, FaSync, FaMapMarkerAlt, FaPlus,
  FaComments, FaRobot, FaStar, FaInfoCircle, FaClock, FaCheckSquare,
  FaThumbsUp, FaCamera, FaBell, FaSuitcase, FaDollarSign, FaListAlt,
  FaMap, FaRoute, FaUsers, FaSearch, FaMagic, FaLightbulb, FaArrowRight,
  FaCheckCircle, FaExclamationTriangle, FaGlobe, FaLanguage, FaVideo
} from 'react-icons/fa';
import SEO from './SEO';
import InternalLinks from './InternalLinks';

const HowTo = ({ onBack, onViewVideos, onGetStarted, onNavigate }) => {
  const [openSection, setOpenSection] = useState(null);
  const [activeCategory, setActiveCategory] = useState('all');

  const categories = [
    { id: 'all', label: 'All Topics', icon: FaBook },
    { id: 'itinerary', label: 'Itinerary', icon: FaRoute },
    { id: 'attractions', label: 'Attractions', icon: FaMapMarkerAlt },
    { id: 'collaboration', label: 'Collaboration', icon: FaUsers },
    { id: 'tracking', label: 'Tracking', icon: FaCheckSquare },
    { id: 'tools', label: 'Tools & Extras', icon: FaSuitcase },
  ];

  const guides = [
    // Itinerary Management
    {
      category: 'itinerary',
      icon: FaPlane,
      iconColor: 'from-primary-500 to-secondary-500',
      title: 'Creating an Itinerary',
      description: 'Generate your personalized AI-powered travel plan',
      steps: [
        { title: 'Click "Create New Itinerary"', detail: 'From your dashboard, click the "Create New Itinerary" button or the + icon in the header.' },
        { title: 'Enter Your Locations', detail: 'Enter your start location and destination. You can also add stops along the way.' },
        { title: 'Set Your Travel Dates', detail: 'Select your start and end dates. The AI will optimize activities based on your trip length.' },
        { title: 'Choose Your Interests', detail: 'Select categories that interest you: Adventure, Culture, Food, Relaxation, Nature, Nightlife, etc.' },
        { title: 'Set Your Preferences', detail: 'Choose your food preference (vegetarian, vegan, halal, etc.) and transportation mode (driving, flying, train, etc.).' },
        { title: 'Generate Your Itinerary', detail: 'Click "Generate" and wait 15-30 seconds while our AI creates your personalized day-by-day plan.' },
      ],
      tips: ['Be specific with your destination for better results', 'Select multiple interests for a diverse itinerary', 'You can always modify the itinerary after generation']
    },
    {
      category: 'itinerary',
      icon: FaEdit,
      iconColor: 'from-amber-500 to-orange-500',
      title: 'Modifying Your Itinerary',
      description: 'Customize and edit your travel plan',
      steps: [
        { title: 'Open Your Itinerary', detail: 'Click on any itinerary from your dashboard to view its details.' },
        { title: 'Use the AI Chatbot', detail: 'Click the chat icon to request changes like "Add a coffee shop after the museum" or "Move dinner to 7 PM".' },
        { title: 'Edit Activity Times', detail: 'Click on the time field of any activity to change when you plan to visit.' },
        { title: 'Mark Activities as Visited', detail: 'Check off activities as you complete them to track your progress.' },
        { title: 'Delete Unwanted Items', detail: 'Remove activities you no longer want to visit from your plan.' },
      ],
      tips: ['Use the AI chatbot to make complex changes to your itinerary', 'Changes are visible to all group members', 'Consider time between locations when planning']
    },
    {
      category: 'itinerary',
      icon: FaSync,
      iconColor: 'from-green-500 to-teal-500',
      title: 'Reoptimizing Your Itinerary',
      description: 'Get fresh suggestions for places you\'ve already visited',
      steps: [
        { title: 'Open Your Itinerary', detail: 'Navigate to the itinerary you want to optimize.' },
        { title: 'Mark Visited Attractions', detail: 'Use the checkboxes next to attractions to mark places you\'ve already visited.' },
        { title: 'Click "Re-optimize"', detail: 'Find the "Re-optimize" button - it becomes active once you\'ve marked at least one attraction as visited.' },
        { title: 'Wait for Regeneration', detail: 'The AI will replace visited attractions with new suggestions and optimize your route.' },
        { title: 'View Updated Itinerary', detail: 'The page will reload automatically to show your updated itinerary with fresh suggestions.' },
      ],
      tips: ['Mark attractions as visited before re-optimizing to get new suggestions', 'The AI considers your remaining unvisited attractions when suggesting replacements', 'You can re-optimize multiple times as your trip progresses']
    },

    // Attractions
    {
      category: 'attractions',
      icon: FaSearch,
      iconColor: 'from-blue-500 to-indigo-500',
      title: 'Exploring Attractions Between Two Points',
      description: 'Discover hidden gems along your route',
      steps: [
        { title: 'Open Your Itinerary', detail: 'Navigate to the day view of your itinerary.' },
        { title: 'Click "Explore Between"', detail: 'Between any two activities, click the "Explore" or compass icon that appears.' },
        { title: 'View Nearby Attractions', detail: 'A map will show attractions, restaurants, and points of interest along your route.' },
        { title: 'Filter Results', detail: 'Use filters to show only restaurants, landmarks, activities, or specific categories.' },
        { title: 'Add to Itinerary', detail: 'Click the + button on any attraction to add it to your itinerary at that location.' },
      ],
      tips: ['Great for finding lunch spots between morning and afternoon activities', 'Shows estimated detour time for each attraction', 'Ratings and reviews help you choose the best stops']
    },
    {
      category: 'attractions',
      icon: FaPlus,
      iconColor: 'from-emerald-500 to-green-500',
      title: 'Adding New Attractions to Itinerary',
      description: 'Manually add places you want to visit',
      steps: [
        { title: 'Click "Add Activity"', detail: 'In your itinerary day view, click the "+" or "Add Activity" button.' },
        { title: 'Search or Enter Details', detail: 'Search for a place by name, or manually enter custom activity details.' },
        { title: 'Select from Suggestions', detail: 'If searching, choose from the autocomplete suggestions that appear.' },
        { title: 'Set Time & Duration', detail: 'Specify when you plan to visit and how long you\'ll spend there.' },
        { title: 'Add Notes (Optional)', detail: 'Include any personal notes, reservation numbers, or reminders.' },
        { title: 'Confirm Addition', detail: 'Click "Add" to insert the activity into your itinerary.' },
      ],
      tips: ['You can add custom activities not in our database', 'The AI will adjust surrounding activities if needed', 'Added attractions are saved for future reference']
    },
    {
      category: 'attractions',
      icon: FaStar,
      iconColor: 'from-yellow-500 to-amber-500',
      title: 'Checking Reviews on Attractions',
      description: 'Read what others say about places',
      steps: [
        { title: 'Find the Reviews Button', detail: 'Look for the "Reviews" button next to any attraction in your itinerary.' },
        { title: 'Click to Open Menu', detail: 'A dropdown will appear with links to popular review sites.' },
        { title: 'Choose a Review Platform', detail: 'Select Google, Yelp, or TripAdvisor to read reviews on that platform.' },
        { title: 'Read Reviews', detail: 'The review site opens in a new tab with reviews for that attraction.' },
      ],
      tips: ['Google Maps often has the most reviews', 'TripAdvisor is great for tourist attractions', 'Yelp is best for restaurants and local businesses']
    },
    {
      category: 'attractions',
      icon: FaInfoCircle,
      iconColor: 'from-cyan-500 to-blue-500',
      title: 'Looking Up More Info on Attractions',
      description: 'Get detailed information about any place',
      steps: [
        { title: 'Click the Info Icon', detail: 'On any attraction, click the "i" icon or "More Info" link.' },
        { title: 'View Full Details', detail: 'See address, phone number, website, opening hours, and description.' },
        { title: 'Check Opening Hours', detail: 'View detailed hours for each day of the week, including holidays.' },
        { title: 'See Pricing Info', detail: 'Find admission costs, ticket types, and any discount information.' },
        { title: 'Get Directions', detail: 'Click the directions link to open navigation in Google Maps.' },
      ],
      tips: ['Opening hours are regularly updated', 'Links to official websites help with bookings', 'Contact info is useful for reservations']
    },
    {
      category: 'attractions',
      icon: FaClock,
      iconColor: 'from-violet-500 to-purple-500',
      title: 'Setting Time on Attractions',
      description: 'Schedule when to visit each place',
      steps: [
        { title: 'Find the Activity', detail: 'Locate the activity in your itinerary day view.' },
        { title: 'Click on the Time Field', detail: 'Click the time display next to the activity.' },
        { title: 'Select Your Time', detail: 'Use the time picker to set when you plan to visit.' },
        { title: 'Save the Change', detail: 'Your new time is saved automatically.' },
      ],
      tips: ['Consider travel time between locations', 'Popular attractions may need more time', 'Plan meal times around your activities']
    },
    {
      category: 'attractions',
      icon: FaMapMarkerAlt,
      iconColor: 'from-red-500 to-pink-500',
      title: 'Using the Attractions Tab',
      description: 'Browse attractions not yet in your itinerary',
      steps: [
        { title: 'Open Your Itinerary', detail: 'Navigate to your itinerary details page.' },
        { title: 'Click "Attractions" Tab', detail: 'Find and click the Attractions tab in the navigation.' },
        { title: 'Browse Categories', detail: 'View attractions organized by category: Museums, Parks, Restaurants, etc.' },
        { title: 'Search & Filter', detail: 'Use search to find specific places or filter by rating, distance, or type.' },
        { title: 'Add to Itinerary', detail: 'Click "Add to Day X" to include any attraction in your plan.' },
      ],
      tips: ['Shows attractions near your planned locations', 'Ratings help prioritize what to visit', 'Can save favorites for future trips']
    },

    // Collaboration Features
    {
      category: 'collaboration',
      icon: FaComments,
      iconColor: 'from-indigo-500 to-blue-500',
      title: 'Using Group Chat',
      description: 'Communicate with your travel companions',
      steps: [
        { title: 'Open Your Shared Itinerary', detail: 'Navigate to an itinerary shared with your group.' },
        { title: 'Click the Chat Icon', detail: 'Find the green chat icon with users in the bottom corner of the itinerary view.' },
        { title: 'Send Messages', detail: 'Type your message and press Enter or click Send.' },
        { title: 'Make Suggestions', detail: 'Suggest changes to the itinerary like "Add the Louvre to Day 2" - the owner can apply them with the magic wand.' },
        { title: 'Delete Messages', detail: 'Hover over your own messages to delete them if needed.' },
      ],
      features: [
        'Real-time messaging with all group members',
        'Magic wand feature for owners to apply suggestions',
        'Typing indicators to see when others are typing',
        'Message deletion for your own messages'
      ],
      warning: 'Note: Destination cannot be changed via group chat once the itinerary is created. To change destinations, create a new itinerary.'
    },
    {
      category: 'collaboration',
      icon: FaMagic,
      iconColor: 'from-purple-500 to-violet-500',
      title: 'Using the Magic Wand in Group Chat',
      description: 'Apply group suggestions to your itinerary (Owner only)',
      steps: [
        { title: 'Open Group Chat', detail: 'Click the Group Chat icon (green button with users icon) to open the chat window.' },
        { title: 'Read Suggestions', detail: 'Group members can send suggestions like "Add the Louvre Museum to Day 2" or "Let\'s have dinner at 7 PM instead".' },
        { title: 'Find the Magic Wand', detail: 'Hover over any message to reveal action buttons. The magic wand icon appears on the right side of each message.' },
        { title: 'Click the Wand', detail: 'Click the magic wand icon on the message containing the suggestion you want to apply.' },
        { title: 'Review the Confirmation', detail: 'A confirmation dialog will appear showing the suggestion and asking if you want to apply it.' },
        { title: 'Confirm Application', detail: 'Click "Yes, Apply" to send the suggestion to AI for processing, or "Cancel" to dismiss.' },
        { title: 'Wait for Regeneration', detail: 'The itinerary will regenerate incorporating the suggestion. This may take a few seconds.' },
      ],
      tips: [
        'Only the itinerary owner can use the magic wand feature',
        'The AI interprets natural language suggestions intelligently',
        'You can apply multiple suggestions one at a time',
        'Group members are notified when their suggestions are applied',
        'Complex suggestions work best when they\'re clear and specific'
      ],
      warning: 'Note: The magic wand cannot change the destination city. Suggestions to visit a different city will not be applied.'
    },
    {
      category: 'collaboration',
      icon: FaRobot,
      iconColor: 'from-green-500 to-emerald-500',
      title: 'Using the AI Chatbot',
      description: 'Get help and make changes through conversation',
      steps: [
        { title: 'Open the Chatbot', detail: 'Click the robot icon or "AI Assistant" button in your itinerary.' },
        { title: 'Ask Questions', detail: 'Type questions like "What restaurants are near the Eiffel Tower?" or "Is this museum good for kids?"' },
        { title: 'Request Changes', detail: 'Say "Add a coffee shop after the museum" or "Move dinner to 7 PM"' },
        { title: 'Get Recommendations', detail: 'Ask "What should I do on Day 3?" or "Suggest a romantic dinner spot"' },
        { title: 'Review Suggestions', detail: 'The AI will suggest changes that you can accept, modify, or decline.' },
        { title: 'Apply Changes', detail: 'Confirm to apply the AI\'s suggested modifications to your itinerary.' },
      ],
      examples: [
        '"Add a lunch break between the museum and park"',
        '"What\'s the best time to visit the Colosseum?"',
        '"Find a vegetarian restaurant near my hotel"',
        '"Rearrange Day 2 to minimize walking"',
        '"What should I pack for this trip?"'
      ],
      warning: 'Note: The AI chatbot cannot change the destination city after your itinerary is created. Create a new itinerary for different destinations.'
    },
    {
      category: 'collaboration',
      icon: FaUsers,
      iconColor: 'from-orange-500 to-red-500',
      title: 'Inviting Group Members',
      description: 'Share your itinerary with travel companions',
      steps: [
        { title: 'Open Your Itinerary', detail: 'Navigate to the itinerary you want to share.' },
        { title: 'Click the Members Icon', detail: 'Find the group/users icon in the itinerary toolbar.' },
        { title: 'Enter Email Address', detail: 'Type the email address of the person you want to invite.' },
        { title: 'Send Invitation', detail: 'Click invite to send them access to view and chat on your itinerary.' },
        { title: 'Manage Members', detail: 'View all members and remove access if needed.' },
      ],
      tips: ['Invited members can view the itinerary and use group chat', 'Only the owner can make changes to the itinerary', 'Members can suggest changes via group chat']
    },

    // Tracking Features
    {
      category: 'tracking',
      icon: FaCheckSquare,
      iconColor: 'from-teal-500 to-cyan-500',
      title: 'Marking Attractions as Visited',
      description: 'Track your progress through the itinerary',
      steps: [
        { title: 'Find the Activity', detail: 'Locate the activity you\'ve completed in your itinerary.' },
        { title: 'Click the Checkbox', detail: 'Click the checkbox or "Mark Complete" button next to the activity.' },
        { title: 'View Progress', detail: 'Your overall trip progress updates in the progress bar.' },
        { title: 'Unmark if Needed', detail: 'Click again to unmark if you didn\'t actually visit.' },
      ],
      tips: ['Completed activities are visually distinguished', 'Progress syncs across all your devices', 'Group members can see shared progress']
    },
    {
      category: 'tracking',
      icon: FaThumbsUp,
      iconColor: 'from-pink-500 to-rose-500',
      title: 'Rating and Commenting on Itineraries',
      description: 'Share your feedback on the AI-generated plan',
      steps: [
        { title: 'Complete Your Trip', detail: 'After finishing your trip (or significant portion), you can rate it.' },
        { title: 'Click "Rate This Trip"', detail: 'Find the rating button in your itinerary or dashboard.' },
        { title: 'Select Star Rating', detail: 'Rate from 1-5 stars based on how well the itinerary worked.' },
        { title: 'Write Your Review', detail: 'Share what worked well, what could improve, and tips for others.' },
        { title: 'Submit Feedback', detail: 'Your feedback helps improve our AI for future itineraries.' },
      ],
      tips: ['Be specific about what worked and what didn\'t', 'Your feedback improves recommendations for everyone', 'You can update your review later']
    },
    {
      category: 'tracking',
      icon: FaCamera,
      iconColor: 'from-fuchsia-500 to-purple-500',
      title: 'Uploading Pictures',
      description: 'Document your trip with photos',
      steps: [
        { title: 'Open Photos/Gallery Section', detail: 'Click the Photos or Gallery tab in your itinerary.' },
        { title: 'Click "Upload"', detail: 'Click the upload button or drag-and-drop images.' },
        { title: 'Select Photos', detail: 'Choose photos from your device. Multiple selection supported.' },
        { title: 'Link to Activities', detail: 'Optionally link photos to specific activities in your itinerary.' },
        { title: 'Add Captions', detail: 'Add descriptions or captions to remember the moment.' },
        { title: 'Share with Group', detail: 'Uploaded photos are visible to all group members.' },
      ],
      tips: ['Photos are organized by day automatically', 'High-res images are optimized for storage', 'Download all photos as a zip file anytime']
    },

    // Tools & Extras
    {
      category: 'tools',
      icon: FaSuitcase,
      iconColor: 'from-amber-500 to-yellow-500',
      title: 'Using the Packing Tab',
      description: 'Never forget essential items',
      steps: [
        { title: 'Open Packing Tab', detail: 'Click the "Packing" tab in your itinerary view.' },
        { title: 'Generate Packing List', detail: 'Click "Generate Packing List" to get AI-powered suggestions based on your trip.' },
        { title: 'Review Suggestions', detail: 'The AI considers your destination, activities, and weather forecast.' },
        { title: 'Customize Your List', detail: 'Add custom items, remove unnecessary ones, or create new categories.' },
        { title: 'Check Off Items', detail: 'Mark items as packed as you prepare for your trip.' },
      ],
      tips: ['Suggestions are based on your destination and weather', 'Add custom items for your specific needs', 'Track your packing progress with the progress bar']
    },
    {
      category: 'tools',
      icon: FaDollarSign,
      iconColor: 'from-green-500 to-lime-500',
      title: 'Tracking Expenses',
      description: 'Monitor your trip spending',
      steps: [
        { title: 'Open Expenses Tab', detail: 'Click the "Expenses" tab in your itinerary.' },
        { title: 'Set a Budget', detail: 'Enter your total trip budget to track against.' },
        { title: 'Add an Expense', detail: 'Click "Add Expense" and enter: description, amount, category, date.' },
        { title: 'Upload Receipts', detail: 'Take a photo or upload a receipt image - AI will automatically extract the amount and details.' },
        { title: 'View Summary', detail: 'See spending breakdown by category and track against your budget.' },
      ],
      tips: ['Supports multiple currencies', 'AI can scan receipts and auto-fill expense details', 'Export expense report as PDF or CSV']
    },
    {
      category: 'tools',
      icon: FaPlane,
      iconColor: 'from-sky-500 to-blue-500',
      title: 'Managing Flights in Your Itinerary',
      description: 'Add and track flight information',
      steps: [
        { title: 'Open Flights Tab', detail: 'Click the "Flights" tab in your itinerary.' },
        { title: 'Add a Flight', detail: 'Click "Add Flight" and enter your flight number and date.' },
        { title: 'Auto-Fill Details', detail: 'Enter your flight number and the system will try to auto-fill departure/arrival times, airports, and terminals.' },
        { title: 'Upload Tickets', detail: 'Attach boarding passes or e-ticket images. You can also scan tickets with AI to auto-extract details.' },
        { title: 'Organize by Type', detail: 'Separate your outbound, return, and any connecting flights.' },
      ],
      tips: ['Add return flights too for complete coverage', 'Upload ticket images and let AI extract the details', 'You can manually edit any auto-filled information']
    },
    {
      category: 'tools',
      icon: FaRoute,
      iconColor: 'from-orange-500 to-amber-500',
      title: 'Adding Layovers (Multiple Flights)',
      description: 'Manage connecting flights and layover details',
      steps: [
        { title: 'Open Flights Tab', detail: 'Navigate to the "Flights" tab in your itinerary.' },
        { title: 'Add Your First Flight', detail: 'Click "Add Flight" and enter your first flight segment details (departure city to layover city).' },
        { title: 'Click "Add Layover" or "Add Connection"', detail: 'After adding the first flight, click the "Add Layover" button to add a connecting flight.' },
        { title: 'Enter Connecting Flight Details', detail: 'Add the second flight segment from your layover city to your next destination.' },
        { title: 'Repeat for Multiple Layovers', detail: 'If you have more than one layover, continue adding flight segments for each connection.' },
        { title: 'View Complete Journey', detail: 'Your full flight path displays as a connected journey showing all segments, layover times, and total travel duration.' },
      ],
      tips: [
        'The system calculates total layover time between flights automatically',
        'Layover warnings alert you if connection time is too short',
        'All flight segments sync to your itinerary timeline',
        'You can add different airlines for each segment',
        'Upload boarding passes for each flight segment separately'
      ]
    },
    {
      category: 'tools',
      icon: FaListAlt,
      iconColor: 'from-slate-500 to-gray-500',
      title: 'Using the Checklist Tab',
      description: 'View travel preparation tasks',
      steps: [
        { title: 'Open Checklist Tab', detail: 'Click the "Checklist" tab in your itinerary.' },
        { title: 'View Extracted Items', detail: 'The AI automatically extracts checklist items from your itinerary (visa requirements, bookings needed, etc.).' },
        { title: 'Check Off Items', detail: 'Click the checkbox next to items as you complete them.' },
        { title: 'Track Progress', detail: 'Use the checklist to ensure you\'ve completed all pre-trip preparations.' },
      ],
      tips: ['Checklist items are auto-generated based on your destination', 'Great for pre-trip preparation', 'Items include things like visas, vaccinations, and reservations']
    },
    {
      category: 'tools',
      icon: FaMap,
      iconColor: 'from-rose-500 to-red-500',
      title: 'Using Google Maps for Directions',
      description: 'Navigate between attractions',
      steps: [
        { title: 'View Your Itinerary Map', detail: 'Click the "Map" view to see all your activities plotted.' },
        { title: 'Click "Directions"', detail: 'On any activity, click the directions or navigation icon.' },
        { title: 'Choose Transport Mode', detail: 'Select walking, driving, public transit, or cycling.' },
        { title: 'Open in Google Maps', detail: 'Click "Open in Maps" to launch Google Maps with the route.' },
        { title: 'View All Day Routes', detail: 'See the complete walking/driving route for an entire day.' },
        { title: 'Download Offline Maps', detail: 'Save map areas for offline use during your trip.' },
      ],
      tips: ['Transit directions show schedules and fares', 'Walking directions account for pedestrian routes', 'Offline maps essential for areas with poor connectivity']
    },
    {
      category: 'tools',
      icon: FaLanguage,
      iconColor: 'from-indigo-500 to-purple-500',
      title: 'Translating Your Itinerary',
      description: 'View your travel plan in different languages',
      steps: [
        { title: 'Open Your Itinerary', detail: 'Navigate to the itinerary you want to translate.' },
        { title: 'Find the Translate Option', detail: 'Look for the translate icon (globe or language icon) in the itinerary toolbar or settings.' },
        { title: 'Select Target Language', detail: 'Choose from available languages: Spanish, French, German, Italian, Portuguese, Japanese, Chinese, Korean, and more.' },
        { title: 'View Translated Content', detail: 'Your itinerary content including attraction names, descriptions, and tips will be displayed in the selected language.' },
        { title: 'Download Translated Version', detail: 'Export or print the translated itinerary to take with you on your trip.' },
        { title: 'Switch Back Anytime', detail: 'You can switch back to the original language or choose a different language at any time.' },
      ],
      tips: [
        'Great for sharing itineraries with travel companions who speak different languages',
        'Helpful when traveling to countries where you don\'t speak the local language',
        'Translated versions include local names for attractions when available',
        'Print translated itinerary to show to locals for directions'
      ]
    },
  ];

  const filteredGuides = activeCategory === 'all'
    ? guides
    : guides.filter(guide => guide.category === activeCategory);

  const toggleSection = (index) => {
    setOpenSection(openSection === index ? null : index);
  };

  // Generate HowTo Schema for SEO
  const howToSchema = {
    "@context": "https://schema.org",
    "@type": "HowTo",
    "name": "How to Use ItinerarySeek",
    "description": "Complete guide to using ItinerarySeek's AI-powered travel planning features",
    "step": guides.slice(0, 10).map((guide, index) => ({
      "@type": "HowToStep",
      "name": guide.title,
      "text": guide.description,
      "position": index + 1
    }))
  };

  return (
    <>
      <SEO
        title="How To Guide - Learn to Use ItinerarySeek"
        description="Step-by-step guides on how to use ItinerarySeek. Learn to create AI itineraries, manage group trips, track expenses, use the chatbot, and make the most of your travel planning."
        canonical={`${SEO_BASE_URL}/howto`}
      />

      {/* HowTo Schema */}
      <script type="application/ld+json">
        {JSON.stringify(howToSchema)}
      </script>

      <div className="min-h-screen bg-gradient-to-br from-neutral-50 via-white to-primary-50">
        {/* Hero Section */}
        <div className="relative overflow-hidden">
          {/* Animated Background Elements */}
          <div className="absolute inset-0 overflow-hidden pointer-events-none">
            <div className="absolute -top-40 -right-40 w-80 h-80 bg-gradient-to-br from-primary-200 to-secondary-200 rounded-full opacity-30 blur-3xl animate-pulse"></div>
            <div className="absolute -bottom-40 -left-40 w-96 h-96 bg-gradient-to-tr from-secondary-200 to-primary-200 rounded-full opacity-30 blur-3xl animate-pulse" style={{ animationDelay: '1s' }}></div>
            <FaPlane className="absolute top-20 right-1/4 text-primary-100 text-6xl transform rotate-12 opacity-50" />
            <FaMapMarkerAlt className="absolute bottom-20 left-1/4 text-secondary-100 text-5xl opacity-40" />
          </div>

          <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 py-16 relative">
            {/* Back Button */}
            <button
              onClick={onBack}
              className="mb-8 flex items-center gap-2 text-neutral-600 hover:text-primary-500 transition-colors group"
            >
              <span className="transform group-hover:-translate-x-1 transition-transform">&larr;</span>
              Back
            </button>

            {/* Main Hero */}
            <div className="text-center mb-12">
              <div className="flex items-center justify-center gap-4 mb-6">
                <span className="h-px w-12 bg-gradient-to-r from-transparent to-primary-400"></span>
                <span className="text-primary-600 text-sm uppercase tracking-widest font-medium flex items-center gap-2">
                  <FaBook className="text-secondary-500" />
                  User Guide
                </span>
                <span className="h-px w-12 bg-gradient-to-l from-transparent to-primary-400"></span>
              </div>

              <h1 className="text-4xl md:text-5xl font-bold mb-6">
                <span className="bg-gradient-to-r from-primary-500 via-secondary-500 to-primary-500 bg-clip-text text-transparent">
                  How Do I...?
                </span>
              </h1>

              <p className="text-lg text-neutral-600 max-w-2xl mx-auto leading-relaxed">
                Everything you need to know about using ItinerarySeek. From creating your first itinerary to
                mastering advanced features, we've got you covered.
              </p>

              <div className="flex flex-wrap items-center justify-center gap-4 mt-6 text-sm">
                <a href="/register" onClick={(e) => { e.preventDefault(); onGetStarted && onGetStarted(); }} className="text-primary-600 hover:text-primary-700 font-medium hover:underline">Get started free</a>
                <span className="text-neutral-300">·</span>
                <a href="/faq" onClick={(e) => { e.preventDefault(); onNavigate && onNavigate('faq'); }} className="text-primary-600 hover:text-primary-700 font-medium hover:underline">FAQ</a>
                <span className="text-neutral-300">·</span>
                <a href="/pricing" onClick={(e) => { e.preventDefault(); onNavigate && onNavigate('pricing'); }} className="text-primary-600 hover:text-primary-700 font-medium hover:underline">View pricing</a>
                <span className="text-neutral-300">·</span>
                <a href="/contact" onClick={(e) => { e.preventDefault(); onNavigate && onNavigate('contact'); }} className="text-primary-600 hover:text-primary-700 font-medium hover:underline">Contact support</a>
              </div>

            </div>

            {/* Category Filter */}
            <div className="flex flex-wrap justify-center gap-3 mb-8">
              {categories.map((category) => {
                const Icon = category.icon;
                return (
                  <button
                    key={category.id}
                    onClick={() => {
                      setActiveCategory(category.id);
                      setOpenSection(null);
                    }}
                    className={`flex items-center gap-2 px-4 py-2 rounded-full text-sm font-medium transition-all duration-300 ${
                      activeCategory === category.id
                        ? 'bg-gradient-to-r from-primary-500 to-secondary-500 text-white shadow-lg'
                        : 'bg-white text-neutral-600 hover:bg-neutral-100 border border-neutral-200'
                    }`}
                  >
                    <Icon className="text-sm" />
                    {category.label}
                  </button>
                );
              })}
              {/* Videos Button */}
              <button
                onClick={onViewVideos}
                className="flex items-center gap-2 px-4 py-2 rounded-full text-sm font-medium transition-all duration-300 bg-gradient-to-r from-primary-500 to-secondary-500 text-white shadow-lg hover:shadow-xl hover:-translate-y-0.5"
              >
                <FaVideo className="text-sm" />
                Videos
              </button>
              {/* Get Started Free Button */}
              {onGetStarted && (
                <button
                  onClick={onGetStarted}
                  className="bg-gradient-to-r from-primary-500 via-accent-500 to-adventure-500 text-white px-6 py-2.5 rounded-xl font-semibold shadow-md hover:shadow-glow-primary hover:scale-105 transition-all duration-300 animate-gradient-x bg-[length:200%_auto]"
                >
                  Get Started Free
                </button>
              )}
            </div>

            {/* Results Count */}
            <p className="text-center text-neutral-500 text-sm mb-8">
              Showing {filteredGuides.length} guide{filteredGuides.length !== 1 ? 's' : ''}
            </p>
          </div>
        </div>

        {/* Guide Cards */}
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 pb-20">
          <div className="space-y-4">
            {filteredGuides.map((guide, index) => {
              const Icon = guide.icon;
              const isOpen = openSection === index;

              return (
                <div
                  key={index}
                  className={`bg-white rounded-2xl border overflow-hidden transition-all duration-300 ${
                    isOpen
                      ? 'border-primary-200 shadow-xl'
                      : 'border-neutral-100 shadow-card hover:shadow-card-hover hover:border-neutral-200'
                  }`}
                >
                  {/* Header */}
                  <button
                    onClick={() => toggleSection(index)}
                    className="w-full px-6 py-5 text-left flex items-center gap-4"
                  >
                    <div className={`w-12 h-12 rounded-xl bg-gradient-to-r ${guide.iconColor} flex items-center justify-center flex-shrink-0 shadow-md`}>
                      <Icon className="text-xl text-white" />
                    </div>
                    <div className="flex-1 min-w-0">
                      <h3 className={`font-semibold text-lg transition-colors ${
                        isOpen ? 'text-primary-600' : 'text-neutral-800'
                      }`}>
                        {guide.title}
                      </h3>
                      <p className="text-neutral-500 text-sm truncate">{guide.description}</p>
                    </div>
                    <div className="flex items-center gap-3">
                      {guide.planRequired && (
                        <span className="hidden sm:inline-flex items-center gap-1 px-2 py-1 bg-amber-100 text-amber-700 rounded-full text-xs font-medium">
                          <FaStar className="text-xs" />
                          {guide.planRequired}
                        </span>
                      )}
                      <span className={`w-8 h-8 rounded-full flex items-center justify-center transition-all duration-300 ${
                        isOpen
                          ? 'bg-gradient-to-r from-primary-500 to-secondary-500 text-white'
                          : 'bg-neutral-100 text-neutral-500'
                      }`}>
                        <FaChevronDown className={`text-sm transition-transform duration-300 ${isOpen ? 'rotate-180' : ''}`} />
                      </span>
                    </div>
                  </button>

                  {/* Expandable Content */}
                  <div className={`transition-all duration-300 ease-in-out overflow-hidden ${
                    isOpen ? 'max-h-[2000px] opacity-100' : 'max-h-0 opacity-0'
                  }`}>
                    <div className="px-6 pb-6 pt-0">
                      <div className="h-px bg-gradient-to-r from-primary-100 via-secondary-100 to-transparent mb-6"></div>

                      {/* Warning Banner */}
                      {guide.warning && (
                        <div className="mb-6 p-4 bg-amber-50 border border-amber-200 rounded-xl flex items-start gap-3">
                          <FaExclamationTriangle className="text-amber-500 text-lg flex-shrink-0 mt-0.5" />
                          <p className="text-amber-800 text-sm">{guide.warning}</p>
                        </div>
                      )}

                      {/* Steps */}
                      <div className="space-y-4 mb-6">
                        <h4 className="font-semibold text-neutral-800 flex items-center gap-2">
                          <FaArrowRight className="text-primary-500 text-sm" />
                          Step-by-Step Instructions
                        </h4>
                        <div className="space-y-3">
                          {guide.steps.map((step, stepIndex) => (
                            <div key={stepIndex} className="flex gap-4">
                              <div className="w-8 h-8 rounded-full bg-gradient-to-r from-primary-100 to-secondary-100 flex items-center justify-center flex-shrink-0">
                                <span className="text-primary-600 font-semibold text-sm">{stepIndex + 1}</span>
                              </div>
                              <div>
                                <p className="font-medium text-neutral-800">{step.title}</p>
                                <p className="text-neutral-600 text-sm mt-1">{step.detail}</p>
                              </div>
                            </div>
                          ))}
                        </div>
                      </div>

                      {/* Features (for collaboration guides) */}
                      {guide.features && (
                        <div className="mb-6">
                          <h4 className="font-semibold text-neutral-800 flex items-center gap-2 mb-3">
                            <FaCheckCircle className="text-green-500 text-sm" />
                            Features
                          </h4>
                          <ul className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                            {guide.features.map((feature, featureIndex) => (
                              <li key={featureIndex} className="flex items-center gap-2 text-sm text-neutral-600">
                                <span className="w-1.5 h-1.5 rounded-full bg-primary-400"></span>
                                {feature}
                              </li>
                            ))}
                          </ul>
                        </div>
                      )}

                      {/* Examples (for chatbot) */}
                      {guide.examples && (
                        <div className="mb-6">
                          <h4 className="font-semibold text-neutral-800 flex items-center gap-2 mb-3">
                            <FaComments className="text-blue-500 text-sm" />
                            Example Commands
                          </h4>
                          <div className="bg-neutral-50 rounded-xl p-4 space-y-2">
                            {guide.examples.map((example, exampleIndex) => (
                              <p key={exampleIndex} className="text-neutral-700 text-sm font-mono">
                                {example}
                              </p>
                            ))}
                          </div>
                        </div>
                      )}

                      {/* Tips */}
                      {guide.tips && (
                        <div className="bg-gradient-to-r from-primary-50 to-secondary-50 rounded-xl p-4">
                          <h4 className="font-semibold text-neutral-800 flex items-center gap-2 mb-3">
                            <FaLightbulb className="text-amber-500" />
                            Pro Tips
                          </h4>
                          <ul className="space-y-2">
                            {guide.tips.map((tip, tipIndex) => (
                              <li key={tipIndex} className="flex items-start gap-2 text-sm text-neutral-600">
                                <span className="text-primary-500 mt-1">•</span>
                                {tip}
                              </li>
                            ))}
                          </ul>
                        </div>
                      )}
                    </div>
                  </div>
                </div>
              );
            })}
          </div>

          {/* No Results */}
          {filteredGuides.length === 0 && (
            <div className="text-center py-12">
              <FaSearch className="text-4xl text-neutral-300 mx-auto mb-4" />
              <p className="text-neutral-500">No guides found in this category.</p>
            </div>
          )}
        </div>

        {/* Internal Links for SEO */}
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 pb-8">
          <InternalLinks currentPage="howto" onNavigate={onNavigate} variant="compact" />
        </div>

        {/* Quick Start CTA */}
        <div className="bg-gradient-to-r from-primary-500 via-primary-600 to-secondary-500 relative overflow-hidden">
          <div className="absolute inset-0 overflow-hidden pointer-events-none">
            <div className="absolute top-10 left-10 w-20 h-20 border-2 border-white/20 rounded-full"></div>
            <div className="absolute bottom-10 right-10 w-32 h-32 border-2 border-white/20 rounded-full"></div>
            <FaPlane className="absolute top-1/4 right-1/4 text-white/10 text-6xl transform rotate-12" />
            <FaMapMarkerAlt className="absolute bottom-1/4 left-1/3 text-white/10 text-5xl" />
          </div>

          <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-16 text-center relative">
            <h2 className="text-2xl md:text-3xl font-bold text-white mb-4">
              Ready to Plan Your Adventure?
            </h2>
            <p className="text-white/90 mb-8 max-w-xl mx-auto">
              Put these guides into practice and create your perfect AI-powered itinerary in minutes.
            </p>
            <button
              onClick={onBack}
              className="inline-flex items-center justify-center gap-2 bg-white text-primary-600 px-8 py-4 rounded-xl font-semibold hover:shadow-xl hover:-translate-y-1 transition-all duration-300"
            >
              <FaMagic />
              Create Your Itinerary
            </button>
          </div>
        </div>

        {/* Feature Highlights */}
        <div className="bg-white py-12 border-t border-neutral-100">
          <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="grid grid-cols-2 md:grid-cols-4 gap-6 text-center">
              <div className="p-4">
                <FaRobot className="text-3xl text-primary-500 mx-auto mb-3" />
                <h3 className="font-semibold text-neutral-800 mb-1">AI Assistant</h3>
                <p className="text-sm text-neutral-500">Get help anytime</p>
              </div>
              <div className="p-4">
                <FaUsers className="text-3xl text-secondary-500 mx-auto mb-3" />
                <h3 className="font-semibold text-neutral-800 mb-1">Group Planning</h3>
                <p className="text-sm text-neutral-500">Travel together</p>
              </div>
              <div className="p-4">
                <FaRoute className="text-3xl text-accent-500 mx-auto mb-3" />
                <h3 className="font-semibold text-neutral-800 mb-1">Smart Routes</h3>
                <p className="text-sm text-neutral-500">Optimized paths</p>
              </div>
              <div className="p-4">
                <FaGlobe className="text-3xl text-adventure-500 mx-auto mb-3" />
                <h3 className="font-semibold text-neutral-800 mb-1">Worldwide</h3>
                <p className="text-sm text-neutral-500">Any destination</p>
              </div>
            </div>
          </div>
        </div>
      </div>
    </>
  );
};

export default HowTo;
